import { useState, useMemo } from "react";
import { Save } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCreateInvestigationTemplate } from "@/lib/mutations";
import { useInvestigations } from "@/lib/queries";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** Currently selected investigation ids on the visit (saved as template.items). */
  items: string[];
}

/**
 * "Save as template" — snapshots the current selected investigations as a
 * named investigation template. Opened from the Investigations column
 * (next to the Insert Template button). Mirrors SaveAsPrescriptionTemplateDialog:
 * a name input plus a Save button that persists via useCreateInvestigationTemplate
 * (POST /investigations/templates — case-insensitive duplicate-name guard →
 * 409 name_taken toast from the mutation's onError). The visit's selection is
 * not modified.
 */
export function SaveAsInvestigationTemplateDialog({ open, onOpenChange, items }: Props) {
  const [name, setName] = useState("");
  const create = useCreateInvestigationTemplate();
  const { data: invData } = useInvestigations();

  // Resolve the selected ids to catalog names for the preview line.
  const names = useMemo(() => {
    const rows = invData?.rows ?? [];
    const byId = new Map(rows.map((i) => [i.id, i.name]));
    return items.map((id) => byId.get(id) ?? "?");
  }, [items, invData]);

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      await create.mutateAsync({ name: trimmed, items });
      onOpenChange(false);
      setName("");
    } catch { /* onError toast already fired (e.g. duplicate name 409) */ }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Save investigations as template</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <div>
            <Label>Template name *</Label>
            <Input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
              placeholder="e.g. Diabetic workup"
            />
          </div>
          {names.length > 0 && (
            <p className="text-xs text-muted">
              {names.slice(0, 5).join(", ")}{names.length > 5 ? ` +${names.length - 5} more` : ""}
            </p>
          )}
          <p className="text-xs text-muted">
            Saves the current {items.length} selected test{items.length === 1 ? "" : "s"} as a reusable template. The visit's selection is not modified.
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!name.trim() || create.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
            <Save size={14} className="mr-1" /> Save template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
